<template>
  <div :class="['auth-picture', { 'is-mobile': isMobile }]">
    <div class="auth-content">
      <div class="auth-context left">
        <span class="auth-title">{{ localize('auth.page.profile.title') }}</span>
        <span class="auth-subtitle">{{ localize('auth.page.registration.subtitle') }}</span>
      </div>
      <div class="auth-content-wrapper">
        <div v-if="gamForm" class="auth-form images">
          <div class="image">
            <div v-if="false" class="label">
              {{ localize('auth.page.registration.form.profile.label') }}
            </div>

            <gam-image
              class="profile-image"
              v-model="gamForm.form.profileImage"
              :image="gamForm.form.profileImage"
              :add="!imageUploading"
              :can-delete="true"
              :size="GamImageSize.CARD"
              :uploading="imageUploading"
              @expand="expandImage"
              @delete="deleteImage"
            />
          </div>

          <div v-if="false" class="image">
            <div class="label">
              {{ localize('auth.page.registration.form.chessboard.label') }}
            </div>
          </div>
        </div>
      </div>
    </div>

    <gam-expand-image
      class="expand-image"
      v-if="selectedImage"
      ref="expandImageRef"
      v-bind="selectedImage"
      @close="clearSelected"
      @delete="deleteImage"
      can-delete
    />
  </div>
</template>

<script setup lang="ts">
import { BlobSource } from '@/core/data/report/report.type';
import { gambits, localize } from '@/core/gambits';
import { isMobile } from '@/core/helpers/ui.helper';
import { useAppStore } from '@/stores/app.store';
import { useAuthStore } from '@/stores/auth.store';
import { useFormStore } from '@/stores/form.store';
import { useNotificationStore } from '@/stores/notification.store';
import { useSparkStore } from '@/stores/spark.store';
import GamExpandImage from '@/views/components/GamExpandImage.vue';
import GamImage from '@/views/components/GamImage.vue';
import { GamImageSize } from '@/views/composables/constants/components/gamImage.constants';
import { FormId } from '@/views/composables/constants/form.constants';
import type { GamExpandImageType } from '@/views/composables/models/components/GamImage';
import type {
  ProfileImagesForm,
  ProfileImagesValidator,
  ProfileUserForm,
} from '@/views/composables/models/form.interface';
import { storeToRefs } from 'pinia';
import { nextTick, onBeforeMount, reactive, ref, watch } from 'vue';
import { useRouter } from 'vue-router';
GamExpandImage;

const formId = FormId.AUTH_PROFILE_IMAGES;
const isAuthLoading = ref<boolean>(true);
const isLoading = ref<boolean>(false);

const sparkStore = useSparkStore();
const formStore = useFormStore<ProfileImagesForm, ProfileImagesValidator>(formId)();
const { gamForm } = storeToRefs(formStore);
const router = useRouter();
const notificationStore = useNotificationStore();

const selectedImage = ref<GamExpandImageType | null>();
const expandImageRef = ref<InstanceType<typeof GamExpandImage>>();

const authStore = useAuthStore();
const { userDetails } = storeToRefs(authStore);

const appStore = useAppStore();
const { imageUploading } = storeToRefs(appStore);

const getForm = reactive({
  profileImage: null,
  chessboardImage: null,
});

const getRules = {
  profileImage: () => true,
  chessboardImage: () => true,
};

onBeforeMount(async () => {
  formStore.setForm({ form: getForm, rules: getRules });
  await getAuthUser();

  const { firstName, lastName, birthDate, username, rating } = userDetails.value || {};

  // Require login
  if (!authStore.isAuthenticated()) {
    return router.push('/auth');
  }
  // Return to personal details step
  if (!firstName || !lastName || !birthDate || !username) {
    return router.push('/auth/registration');
  }
  // Return to ratings step
  if (!rating) {
    return router.push('/auth/ratings');
  }
  // City selection is required before profile images.
  if (!userDetails.value?.city?.id) {
    return router.push('/auth/location');
  }

  // // Advance to profile if image exists
  // if (profileImageBlobHash) {
  //   return router.push({
  //     name: RouterNameType.PLAYERS,
  //     params: { username },
  //     query: { ...currentLocation.value, zoom: currentZoom.value },
  //   });
  // }
});

const getAuthUser = async (): Promise<void> => {
  await authStore.fetchUserInfo(true);

  if (gamForm.value?.form) {
    gamForm.value.form.profileImage = gamForm.value.form.profileImage || userDetails.value?.profileImageBlobHash || '';
    gamForm.value.form.chessboardImage = userDetails.value?.chessBoardImageBlobHash;
  }

  isAuthLoading.value = false;
};

const expandImage = (hash: string) => {
  selectedImage.value = {
    selectedIndex: 0,
    blobSource: BlobSource.USER_PROFILE,
    images: [
      {
        id: hash,
        imageBlobHash: hash,
        liked: false,
        user: null,
      },
    ],
    canDelete: true,
    closeAfterDelete: true,
  };
  nextTick(() => {
    expandImageRef.value?.show();
  });
};

const clearSelected = () => {
  selectedImage.value = null;
};

const deleteImage = async (hash: string) => {
  if (gamForm.value?.form.profileImage === hash) {
    gamForm.value.form.profileImage = null;
    isLoading.value = true;
    await handleSubmit();
    isLoading.value = false;
  }
};

const handleSubmit = async () => {
  if (!gamForm.value?.form || !userDetails.value) {
    return;
  }

  const payload = { ...userDetails.value, ...gamForm.value?.form } as ProfileUserForm;

  useAppStore().setImageUploading(true);

  await gambits.userService.updateUser(payload);
  await sparkStore.clear();

  useAppStore().setImageUploading(false);
};

// const finishOnboarding = () => {
//   // Return the user to the action they wanted to perform before being redirected to signup
//   // This requires a stored URL in something like authStore.signupIntent. persistent accross sessions?
// };

watch(
  () => userDetails.value?.rating,
  () => {
    notificationStore.initialize();
  },
  { immediate: true },
);

watch(
  () => gamForm.value?.form.profileImage,
  async (newImage, oldImage) => {
    if (!newImage || newImage === oldImage || isAuthLoading.value) return;

    await handleSubmit();
  },
);
</script>

<style lang="scss" scoped>
.auth-picture:not(.is-mobile) {
  min-width: 340px !important;
}

#auth-view .auth-modal .auth-wrapper .auth-content {
  padding-bottom: calc(var(--spacing-xxl) + var(--spacing-md));
}

.auth-content-wrapper {
  width: 100%;
}
</style>
